import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'wouter';
import { Plus, Search, Music2, X, ChevronDown } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { api } from '../lib/api';

const KEYS = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B',
  'Cm', 'C#m', 'Dm', 'D#m', 'Ebm', 'Em', 'Fm', 'F#m', 'Gm', 'G#m', 'Am', 'A#m', 'Bbm', 'Bm'];

const BPM_RANGES = [
  { label: 'All BPM', value: '' },
  { label: '< 80', value: 'lt80' },
  { label: '80 – 100', value: '80-100' },
  { label: '100 – 120', value: '100-120' },
  { label: '120 – 140', value: '120-140' },
  { label: '140+', value: 'gt140' },
];

const SORT_OPTIONS = [
  { label: 'Title (A → Z)', value: 'title-asc' },
  { label: 'Title (Z → A)', value: 'title-desc' },
  { label: 'Artist (A → Z)', value: 'artist-asc' },
  { label: 'Artist (Z → A)', value: 'artist-desc' },
  { label: 'BPM (low → high)', value: 'bpm-asc' },
  { label: 'BPM (high → low)', value: 'bpm-desc' },
];

const LS_KEY = (bandId: number) => `song-library-prefs-${bandId}`;

function loadPrefs(bandId: number) {
  try {
    const raw = localStorage.getItem(LS_KEY(bandId));
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function savePrefs(bandId: number, prefs: Record<string, string>) {
  try { localStorage.setItem(LS_KEY(bandId), JSON.stringify(prefs)); } catch {}
}

function bpmInRange(bpm: number | null, range: string): boolean {
  if (!range) return true;
  if (bpm === null || bpm === undefined) return false;
  if (range === 'lt80') return bpm < 80;
  if (range === 'gt140') return bpm >= 140;
  const [lo, hi] = range.split('-').map(Number);
  return bpm >= lo && bpm < hi;
}

function sortSongs(songs: any[], sortBy: string): any[] {
  const copy = [...songs];
  copy.sort((a, b) => {
    switch (sortBy) {
      case 'title-asc':  return (a.title || '').localeCompare(b.title || '');
      case 'title-desc': return (b.title || '').localeCompare(a.title || '');
      case 'artist-asc': return (a.artist || '').localeCompare(b.artist || '');
      case 'artist-desc': return (b.artist || '').localeCompare(a.artist || '');
      case 'bpm-asc':  return (a.defaultTempo ?? 9999) - (b.defaultTempo ?? 9999);
      case 'bpm-desc': return (b.defaultTempo ?? -1) - (a.defaultTempo ?? -1);
      default: return (a.title || '').localeCompare(b.title || '');
    }
  });
  return copy;
}

export function SongLibrary() {
  const params = useParams<{ bandId: string }>();
  const bandId = Number(params.bandId);
  const queryClient = useQueryClient();

  const prefs = useMemo(() => loadPrefs(bandId), [bandId]);

  const [search, setSearch]     = useState<string>(prefs.search || '');
  const [keyFilter, setKeyFilter]   = useState<string>(prefs.keyFilter || '');
  const [bpmFilter, setBpmFilter]   = useState<string>(prefs.bpmFilter || '');
  const [tagFilter, setTagFilter]   = useState<string>(prefs.tagFilter || '');
  const [sortBy, setSortBy]         = useState<string>(prefs.sortBy || 'title-asc');

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle]       = useState('');
  const [artist, setArtist]     = useState('');
  const [defaultKey, setDefaultKey] = useState('');
  const [defaultTempo, setDefaultTempo] = useState('');
  const [tags, setTags]         = useState('');

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: allSongs = [], isLoading } = useQuery({
    queryKey: ['songs', bandId],
    queryFn: () => api.songs.list(bandId),
  });

  useEffect(() => {
    savePrefs(bandId, { search, keyFilter, bpmFilter, tagFilter, sortBy });
  }, [bandId, search, keyFilter, bpmFilter, tagFilter, sortBy]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const s of allSongs as any[]) {
      for (const t of (s.tags || [])) tagSet.add(t);
    }
    return Array.from(tagSet).sort();
  }, [allSongs]);

  const filteredSongs = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = (allSongs as any[]).filter(s => {
      if (q) {
        const inTitle  = (s.title || '').toLowerCase().includes(q);
        const inArtist = (s.artist || '').toLowerCase().includes(q);
        const inTags   = (s.tags || []).some((t: string) => t.toLowerCase().includes(q));
        if (!inTitle && !inArtist && !inTags) return false;
      }
      if (keyFilter && s.defaultKey !== keyFilter) return false;
      if (bpmFilter && !bpmInRange(s.defaultTempo, bpmFilter)) return false;
      if (tagFilter && !(s.tags || []).includes(tagFilter)) return false;
      return true;
    });
    return sortSongs(result, sortBy);
  }, [allSongs, search, keyFilter, bpmFilter, tagFilter, sortBy]);

  const hasActiveFilters = !!(search || keyFilter || bpmFilter || tagFilter);

  const clearFilters = () => {
    setSearch(''); setKeyFilter(''); setBpmFilter(''); setTagFilter('');
  };

  const createSong = useMutation({
    mutationFn: api.songs.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songs', bandId] });
      setShowCreate(false);
      resetForm();
    },
  });

  function resetForm() {
    setTitle(''); setArtist(''); setDefaultKey(''); setDefaultTempo(''); setTags('');
  }

  const selectClass = "bg-surface-900 border border-surface-700 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none pr-7";

  return (
    <Layout bandId={bandId} bandName={band?.name} title="Song Library">
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Song Library</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Song
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-surface-900 border border-surface-700 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Search title, artist, tags…"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>

          <div className="relative">
            <select value={keyFilter} onChange={e => setKeyFilter(e.target.value)} className={selectClass}>
              <option value="">All Keys</option>
              {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
          </div>

          <div className="relative">
            <select value={bpmFilter} onChange={e => setBpmFilter(e.target.value)} className={selectClass}>
              {BPM_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
          </div>

          {availableTags.length > 0 && (
            <div className="relative">
              <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className={selectClass}>
                <option value="">All Tags</option>
                {availableTags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
          )}

          <div className="relative">
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={selectClass}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : filteredSongs.length === 0 ? (
          <div className="text-center py-16">
            <Music2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            {hasActiveFilters ? (
              <>
                <p className="text-gray-400 mb-3">No songs match your filters.</p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-400 hover:text-primary-300 underline underline-offset-2 transition-colors"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <p className="text-gray-400">No songs yet. Add your first song to get started.</p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredSongs.map((song: any) => (
              <Link key={song.id} href={`/bands/${bandId}/songs/${song.id}`}>
                <div className="flex items-center gap-3 p-3 bg-surface-900 border border-surface-700 rounded-lg hover:border-primary-500/30 transition-colors cursor-pointer group">
                  <Music2 className="w-4 h-4 text-gray-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-primary-300 transition-colors">{song.title}</p>
                    {song.artist && <p className="text-xs text-gray-500 truncate">{song.artist}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                    {(song.tags || []).map((t: string) => (
                      <span key={t} className="text-xs bg-primary-600/20 text-primary-300 px-1.5 py-0.5 rounded hidden md:inline">{t}</span>
                    ))}
                    {song.defaultKey && (
                      <span className="text-xs bg-surface-800 text-gray-400 px-2 py-0.5 rounded">{song.defaultKey}</span>
                    )}
                    {song.defaultTempo && (
                      <span className="text-xs bg-surface-800 text-gray-400 px-2 py-0.5 rounded">{song.defaultTempo} BPM</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Modal open={showCreate} onClose={() => { setShowCreate(false); resetForm(); }} title="Add Song">
          <form
            onSubmit={e => {
              e.preventDefault();
              createSong.mutate({
                bandId,
                title,
                artist: artist || undefined,
                defaultKey: defaultKey || undefined,
                defaultTempo: defaultTempo ? Number(defaultTempo) : undefined,
                tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Artist</label>
              <input value={artist} onChange={e => setArtist(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Key</label>
                <select value={defaultKey} onChange={e => setDefaultKey(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Select…</option>
                  {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tempo (BPM)</label>
                <input type="number" value={defaultTempo} onChange={e => setDefaultTempo(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" min="1" max="300" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tags (comma-separated)</label>
              <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. worship, acoustic, cover" className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <button type="submit" disabled={createSong.isPending} className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm">
              {createSong.isPending ? 'Adding…' : 'Add Song'}
            </button>
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
